/**
 * @name NotFoundPage
 * @description Página 404 - Página não encontrada
 * @author Gabriel M. Guimarães | gabrielmg7
 */

import GradientBackground from '@/components/common/backgrounds/GradientBackground';
import useThemeContext from '@/themes/hooks/useThemeContext';
import { ArrowBack as ArrowBackIcon, Home as HomeIcon, SearchOff as SearchOffIcon } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Container, Typography } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';

const NotFoundPage: React.FC = () => {
     const { theme } = useThemeContext();
     const navigate = useNavigate();

     const handleGoHome = () => {
          navigate('/', { replace: true });
     };

     const handleGoBack = () => {
          navigate(-1);
     };

     return (
          <GradientBackground>
               <Container maxWidth='md'>
                    <Box
                         sx={{
                              minHeight: '100vh',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              p: 2,
                         }}
                    >
                         <Card
                              sx={{
                                   maxWidth: 600,
                                   textAlign: 'center',
                                   backgroundColor: theme.palette.background.paper,
                                   border: `1px solid ${theme.palette.divider}`,
                              }}
                         >
                              <CardContent sx={{ p: 4 }}>
                                   <SearchOffIcon
                                        sx={{
                                             fontSize: 120,
                                             color: theme.palette.warning.main,
                                             mb: 2,
                                        }}
                                   />

                                   <Typography
                                        variant='h2'
                                        component='h1'
                                        gutterBottom
                                        sx={{ fontWeight: 'bold' }}
                                   >
                                        404
                                   </Typography>

                                   <Typography
                                        variant='h5'
                                        component='h2'
                                        gutterBottom
                                   >
                                        Página não encontrada
                                   </Typography>

                                   <Typography
                                        variant='body1'
                                        color='text.secondary'
                                        sx={{ mb: 4 }}
                                   >
                                        A página que você está procurando não existe ou foi movida. Verifique o endereço digitado ou navegue para uma das seções principais.
                                   </Typography>

                                   <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                                        <Button
                                             variant='contained'
                                             startIcon={<HomeIcon />}
                                             onClick={handleGoHome}
                                             sx={{
                                                  backgroundColor: theme.palette.primary.main,
                                                  '&:hover': {
                                                       backgroundColor: theme.palette.primary.dark,
                                                  },
                                             }}
                                        >
                                             Página Inicial
                                        </Button>

                                        <Button
                                             variant='outlined'
                                             startIcon={<ArrowBackIcon />}
                                             onClick={handleGoBack}
                                             sx={{
                                                  borderColor: theme.palette.primary.main,
                                                  color: theme.palette.primary.main,
                                                  '&:hover': {
                                                       borderColor: theme.palette.primary.dark,
                                                       backgroundColor: theme.palette.primary.light + '20',
                                                  },
                                             }}
                                        >
                                             Voltar
                                        </Button>
                                   </Box>

                                   <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                                        <Typography
                                             variant='body2'
                                             color='text.secondary'
                                        >
                                             Precisa de ajuda? Entre em contato com o suporte.
                                        </Typography>
                                   </Box>
                              </CardContent>
                         </Card>
                    </Box>
               </Container>
          </GradientBackground>
     );
};

export { NotFoundPage };
